//
// # LoginScreen
//
// Full-page login screen with Firebase auth methods:
// - Google sign-in (popup)
// - Email magic link (passwordless)
//

import React, { useState, useCallback } from "react";
import { sendLoginLink, signInWithGoogle } from "../firebaseAuth";

interface LoginScreenProps {
  readonly errorMessage?: string;
}

export function LoginScreen({ errorMessage }: LoginScreenProps) {
  const [email, setEmail] = useState("");
  const [linkSent, setLinkSent] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(errorMessage ?? null);

  const handleGoogleSignIn = useCallback(async () => {
    setSending(true);
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Google sign-in failed");
    } finally {
      setSending(false);
    }
  }, []);

  const handleSendLink = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim() || sending) return;
      setSending(true);
      setError(null);
      try {
        await sendLoginLink(email.trim());
        setLinkSent(true);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to send login link"
        );
      } finally {
        setSending(false);
      }
    },
    [email, sending]
  );

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>GenAlpha</h1>

        {linkSent ? (
          <div>
            <p style={styles.message}>
              A sign-in link has been sent to <strong>{email}</strong>.
            </p>
            <p style={styles.hint}>
              Check your email and click the link to sign in.
            </p>
            <button
              style={styles.textButton}
              onClick={() => {
                setLinkSent(false);
                setError(null);
              }}
            >
              Back
            </button>
          </div>
        ) : (
          <>
            {error && <p style={styles.error}>{error}</p>}
            <button
              style={styles.googleButton}
              onClick={() => void handleGoogleSignIn()}
              disabled={sending}
            >
              Sign in with Google
            </button>

            <div style={styles.divider}>
              <span style={styles.dividerText}>or</span>
            </div>

            {showEmailForm ? (
              <form onSubmit={(e) => void handleSendLink(e)}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  style={styles.input}
                  autoFocus
                  required
                />
                <button
                  type="submit"
                  style={styles.button}
                  disabled={sending || !email.trim()}
                >
                  {sending ? "Sending..." : "Send Login Link"}
                </button>
                <button
                  type="button"
                  style={styles.textButton}
                  onClick={() => {
                    setShowEmailForm(false);
                    setError(null);
                  }}
                >
                  Back
                </button>
              </form>
            ) : (
              <button
                style={styles.emailButton}
                onClick={() => setShowEmailForm(true)}
              >
                Sign in with email link
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100vw",
    height: "100vh",
    backgroundColor: "#1a1a2e",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  card: {
    backgroundColor: "#16213e",
    borderRadius: "8px",
    padding: "40px",
    maxWidth: "400px",
    width: "100%",
    boxShadow: "0 4px 24px rgba(0, 0, 0, 0.3)",
  },
  title: {
    color: "#e0e0e0",
    fontSize: "24px",
    fontWeight: 600,
    marginBottom: "24px",
    textAlign: "center" as const,
  },
  message: {
    color: "#b0b0b0",
    fontSize: "14px",
    marginBottom: "16px",
  },
  hint: {
    color: "#808080",
    fontSize: "13px",
    marginBottom: "20px",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    fontSize: "14px",
    backgroundColor: "#0f3460",
    border: "1px solid #1a4080",
    borderRadius: "4px",
    color: "#e0e0e0",
    marginBottom: "12px",
    boxSizing: "border-box" as const,
    outline: "none",
  },
  button: {
    width: "100%",
    padding: "10px",
    fontSize: "14px",
    fontWeight: 500,
    backgroundColor: "#533483",
    color: "#e0e0e0",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    marginBottom: "8px",
  },
  googleButton: {
    width: "100%",
    padding: "10px",
    fontSize: "14px",
    fontWeight: 500,
    backgroundColor: "#1a4080",
    color: "#e0e0e0",
    border: "1px solid #2a5090",
    borderRadius: "4px",
    cursor: "pointer",
  },
  emailButton: {
    width: "100%",
    padding: "10px",
    fontSize: "14px",
    fontWeight: 500,
    backgroundColor: "transparent",
    color: "#b0b0b0",
    border: "1px solid #1a4080",
    borderRadius: "4px",
    cursor: "pointer",
  },
  textButton: {
    background: "none",
    border: "none",
    color: "#6080b0",
    fontSize: "13px",
    cursor: "pointer",
    padding: "4px 0",
    width: "100%",
    textAlign: "center" as const,
  },
  divider: {
    display: "flex",
    alignItems: "center",
    margin: "16px 0",
    borderTop: "1px solid #1a4080",
    position: "relative" as const,
    justifyContent: "center",
  },
  dividerText: {
    color: "#606080",
    fontSize: "12px",
    backgroundColor: "#16213e",
    padding: "0 12px",
    position: "relative" as const,
    top: "-1px",
  },
  error: {
    color: "#e74c3c",
    fontSize: "13px",
    marginBottom: "12px",
  },
};
